import { ComponentProps, FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Checkbox as MuiCheckbox,
  DataTable,
  DataTableContainer,
  InfiniteScroll,
  Text,
} from '@hopskipdrive/react-components';
import { isToday, isTomorrow } from 'date-fns';
import { differenceBy, groupBy, includes } from 'lodash';

import { RIDE_STATES_QUERY } from '../../../../constants';
import { useInfiniteTableScroll } from '../../../../hooks/useInfiniteTableScroll';
import { useRidesTableColumns } from '../../../../hooks/useRidesTableColumns';
import { useTimezone } from '../../../../hooks/useTimezone';
import { useNavigation } from '../../../../shared/hooks/useNavigation';
import { formatDate } from '../../../../utils/helpers';
import { BulkCancelRides } from '../BulkCancel';
import { Params, RideForRiderCentric } from '../types';
import { useBulkCancelRides } from '../useBulkCancelRides';

import { RidesTableContainer } from './RidesTable.styles';

interface GroupedRides {
  [key: string]: RideForRiderCentric[];
}

interface IRidesTable {
  rides: RideForRiderCentric[];
  canDeleteRide: boolean;
  canEditRide: boolean;
  loading: boolean;
  getMoreRides: (query: QueryParams) => void;
  params: Params;
  onRowClick: ({ slug }: { slug: string }) => void;
  riderId: string;
  riderName: string;
  headingHeight?: number;
}

export const RidesTable: FC<IRidesTable> = ({
  rides,
  canDeleteRide,
  canEditRide,
  loading,
  getMoreRides,
  params,
  onRowClick,
  riderId,
  riderName,
  headingHeight,
}) => {
  const [sortedRides, setSortedRides] = useState<GroupedRides>({});
  const [rideIds, setRideIds] = useState<number[]>([]);
  const { selectedRideIds, handleCheckboxSelection, handleCancelRides, cancelRidesResponse, reset } =
    useBulkCancelRides();
  const { pageParams, handlePagination, resetParams } = useInfiniteTableScroll();
  const { navigateTo } = useNavigation();
  const { getDateInTimezone } = useTimezone();
  const columns = useRidesTableColumns(params.state);
  const location = useLocation();

  useEffect(() => {
    const fromDate = params.fromDate && getDateInTimezone(params.fromDate);

    const dateOfQueryParam = fromDate
      ? {
          [getDateGroup(fromDate) || '']: [],
        }
      : {};
    const newGroupedRides = {
      ...dateOfQueryParam,
      ...groupBy(rides, (ride) => getDateGroup(getDateInTimezone(ride.startsAt))),
    };
    const newQuery = !!(fromDate && pageParams.page == 1) || pageParams.page === 1;

    const newRideIds = rides.map((ride) => ride.id);

    setRideIds((prevIds) => (newQuery ? newRideIds : prevIds.concat(newRideIds)));

    setSortedRides((prevRides) => (newQuery ? newGroupedRides : combineRides(prevRides, newGroupedRides)));
  }, [rides]);

  useEffect(() => {
    getMoreRides({ ...pageParams, ...params });
  }, [pageParams]);

  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      return;
    }
    resetParams();
  }, [params, getMoreRides, location.state]);

  useEffect(() => {
    // when there is a cancelRidesResponse, this means there was a successful bulk cancel
    if (cancelRidesResponse) {
      removeRides(selectedRideIds, setSortedRides);
    }
  }, [cancelRidesResponse]);

  let tableActions: ComponentProps<typeof DataTable>['actions'];

  if (params.state === RIDE_STATES_QUERY.upcoming) {
    tableActions = [
      {
        icon: 'baseline-edit-note',
        tooltip: 'Edit Ride',
        allowAction: (ride: Ride) => canEditRide && ride.sharedBillingRole !== 'Residence',
        handleActionClick: (ride: Ride) => navigateTo(`/rides/${ride.slug}/edit`),
      },
    ];
  } else if (params.state === RIDE_STATES_QUERY.completed) {
    tableActions = [
      {
        icon: 'view',
        tooltip: 'View Ride',
        handleActionClick: (ride: Ride) => navigateTo(`/rides/${ride.slug}`, { riderName, riderSlug: riderId }),
      },
    ];
  }

  return (
    <>
      <RidesTableContainer>
        <DataTableContainer heightOffset={headingHeight || 0} minWidth="1630px">
          <InfiniteScroll
            itemCount={rideIds.length}
            pageNumber={pageParams.page}
            perPage={pageParams.perPage}
            handleNextPage={handlePagination}
          >
            <DataTable
              stickyHeader={true}
              isLoading={loading}
              groupedRows={sortedRides}
              actions={tableActions}
              handleRowClick={onRowClick}
              href={({ slug }) => `/rides/${slug}`}
              noRecordsMessage={getNoRidesMessage(params.state)}
              noRecordsForGroupMessage={getNoRidesOnDateMessage()}
              columns={[
                {
                  label: ' ',
                  align: 'left',
                  valueGetter: (rowData) => {
                    const canCancel = rowData.canCancel;

                    return (
                      canCancel && (
                        <BulkCancelCheckBox
                          id={rowData.id}
                          selectedRideIds={selectedRideIds}
                          handleCheckboxSelection={(e) => handleCheckboxSelection(e, rowData.id, rideIds)}
                        />
                      )
                    );
                  },
                  isHidden:
                    includes(
                      [RIDE_STATES_QUERY.billableCanceled, RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.completed],
                      params.state
                    ) || !canDeleteRide,
                },
                ...columns,
              ]}
            />
          </InfiniteScroll>
        </DataTableContainer>
      </RidesTableContainer>

      {canDeleteRide && (
        <BulkCancelRides
          selectedRideIds={selectedRideIds}
          handleCancelRides={handleCancelRides}
          onClose={() => reset()}
          cancelRidesResponse={cancelRidesResponse}
        />
      )}
    </>
  );
};

function removeRides(idsToRemove, setSortedRides) {
  setSortedRides((prevSortedRides) => {
    const sortedRides = {};

    for (const date in prevSortedRides) {
      const rides = prevSortedRides[date].filter((r) => !includes(idsToRemove, r.id));

      if (rides.length > 0) {
        sortedRides[date] = rides;
      }
    }

    return sortedRides;
  });
}

function getDateGroup(date) {
  return (
    (isToday(date) && `TODAY ${formatDate(date, 'LL.dd.yy').toUpperCase()}`) ||
    (isTomorrow(date)
      ? `TOMORROW ${formatDate(date, 'LL.dd.yy').toUpperCase()}`
      : !isToday(date) && !isTomorrow(date) && formatDate(date, 'EEEE LL.dd.yy').toUpperCase())
  );
}

const getNoRidesOnDateMessage = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="26px">
      <Text noWrap>{'No rides on this date'}</Text>
    </Box>
  );
};

const getNoRidesMessage = (state) => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
      <Text textColor="secondaryText" noWrap>{`You don’t have any ${labelForState(
        state
      )} rides that match the criteria`}</Text>
    </Box>
  );
};

const combineRides = (existingRides, newRides) => {
  const combinedRides = existingRides;

  for (const rideDay in newRides) {
    if (existingRides[rideDay]) {
      combinedRides[rideDay] = [
        ...existingRides[rideDay],
        ...differenceBy(newRides[rideDay], existingRides[rideDay], 'slug'),
      ];
    } else {
      combinedRides[rideDay] = newRides[rideDay];
    }
  }

  return combinedRides;
};

const labelForState = (state) => {
  switch (state) {
    case RIDE_STATES_QUERY.upcoming:
      return 'in progress or upcoming';
    case RIDE_STATES_QUERY.allCanceled:
      return 'canceled';
    case RIDE_STATES_QUERY.billableCanceled:
      return 'billable canceled';
    default:
      return state;
  }
};

const BulkCancelCheckBox = ({ id, selectedRideIds, handleCheckboxSelection }) => {
  return (
    <MuiCheckbox
      data-testid="row-checkbox"
      checked={selectedRideIds.indexOf(id) !== -1}
      onClick={handleCheckboxSelection}
    />
  );
};
